import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { getTours, sendWishlistEmail } from '../services/api';
import { useWishlist } from '../hooks/useWishlist';
import { imgErrorFallback } from '../utils/imageFallback';
import SEO from './SEO';

const fmtPrice = (p) => new Intl.NumberFormat('tr-TR').format(p || 0);

const WishlistPage = () => {
  const { ids, remove, clear } = useWishlist();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getTours({ limit: 100 })
      .then((r) => setTours(r.data || []))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const savedTours = tours.filter((t) => ids.includes(t.id));

  const submitEmail = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!email.includes('@')) {
      setError('Lütfen geçerli bir e-posta girin.');
      return;
    }
    if (savedTours.length === 0) {
      setError('Listenizde tur yok.');
      return;
    }
    setSending(true);
    try {
      const res = await sendWishlistEmail({
        email,
        tourIds: savedTours.map((t) => t.id),
      });
      setMessage(res.data?.message || 'Listeniz e-postanıza gönderildi.');
      setEmail('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'E-posta gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-white min-h-screen" data-testid="wishlist-page">
      <SEO title="Beğendiklerim" description="Camption'da ilgilendiğiniz turlar." noIndex />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-[#3D4A26]">Ana Sayfa</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Beğendiklerim</li>
          </ol>
        </nav>

        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B1B1B] leading-tight mb-4 flex items-center gap-3"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          <Heart size={40} fill="#ef4444" stroke="#ef4444" />
          Beğendiklerim
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mb-10">
          {savedTours.length === 0
            ? 'Bir tur görürken kalp simgesine dokunarak buraya kaydedebilirsiniz.'
            : `${savedTours.length} tur seçtiniz. Kişisel listenizi e-postanıza gönderelim, sonra karar verin.`}
        </p>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#3D4A26]" size={28} /></div>
        ) : savedTours.length === 0 ? (
          <div className="text-center py-16">
            <Link
              to="/turlar"
              className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-[#2d3a1a]"
            >
              Turları Keşfet <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
              {savedTours.map((t) => (
                <div key={t.id} className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#3D4A26]/40 transition-colors" data-testid={`wishlist-card-item-${t.id}`}>
                  <div className="flex flex-col sm:flex-row">
                    <Link to={`/tur/${t.slug}`} className="sm:w-40 h-40 sm:h-auto flex-shrink-0 bg-gray-100 block">
                      <img src={t.heroImage} alt={t.heroImageAlt || t.title} onError={imgErrorFallback} loading="lazy" className="w-full h-full object-cover" />
                    </Link>
                    <div className="p-4 flex-1 flex flex-col">
                      <Link to={`/tur/${t.slug}`} className="font-bold text-[#1B1B1B] leading-snug mb-1 hover:text-[#3D4A26]">
                        {t.title}
                      </Link>
                      <p className="text-xs text-gray-500 mb-2">{t.nights} gece · {t.location}</p>
                      <p className="text-sm font-bold text-[#1B1B1B]">
                        {t.currency}{fmtPrice(t.salePrice)} <span className="text-xs font-normal text-gray-500">/ kişi</span>
                      </p>
                      <div className="mt-auto pt-3 flex items-center justify-between">
                        <Link to={`/tur/${t.slug}`} className="text-xs font-semibold text-[#3D4A26] hover:underline inline-flex items-center gap-1">
                          Detay <ArrowRight size={12} />
                        </Link>
                        <button
                          onClick={() => remove(t.id)}
                          data-testid={`wishlist-remove-${t.id}`}
                          className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={12} /> Kaldır
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Email shortlist */}
            <div className="bg-[#FAFAF7] border border-gray-200 rounded-2xl p-6" data-testid="wishlist-email-form">
              <h2 className="text-xl font-bold text-[#1B1B1B] mb-1 flex items-center gap-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                <Mail size={20} /> Listeyi e-postama gönder
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Bu {savedTours.length} turu ilerde tekrar bulmak için tek tıkla kendinize gönderin. Reklam veya bülten göndermeyiz.
              </p>
              <form onSubmit={submitEmail} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] bg-white"
                  required
                  data-testid="wishlist-email-input"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="wishlist-email-send"
                  className="bg-[#3D4A26] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#2d3a1a] text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {sending ? 'Gönderiliyor…' : 'E-posta Gönder'}
                </button>
              </form>
              {message && <p className="mt-3 text-sm text-[#3D4A26] bg-[#f0f4d8] border border-[#C8D843]/30 rounded-lg px-3 py-2" data-testid="wishlist-success">{message}</p>}
              {error && <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

              <button
                type="button"
                onClick={() => { if (window.confirm('Listenizi temizlemek istediğinize emin misiniz?')) clear(); }}
                className="mt-4 text-xs text-gray-500 hover:text-red-600"
              >
                Listeyi tamamen temizle
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default WishlistPage;
